/**
 * Integrytas Group - Per-application page registry extension point.
 *
 * BaseApp ships this as an EMPTY array. Derived applications (scaffolded copies
 * with an `upstream` remote -> BaseApp) own this file and append their own,
 * prefixed page definitions here instead of editing the shared
 * `src/pages/page_registry.ts`. Because BaseApp never modifies this file after
 * creating it, `git merge upstream/master` stays conflict-free for derived apps.
 *
 * Entries are spread into PAGE_REGISTRY by `src/pages/page_registry.ts`, so they
 * are synced to csm_page_master and covered by the screen smoke test exactly
 * like core pages (provided each has a component in APP_PAGE_COMPONENTS).
 */

import type { PageDefinition } from '../pages/page_registry';

export const APP_PAGE_REGISTRY: PageDefinition[] = [
  // NAV.SETTINGS and the demotion of every inherited BaseApp NAV.* group under
  // it now ship natively in core page_registry.ts (§17.E,
  // 20260721000000_page_sync_parent_repair.sql) — the scaffold-era per-group
  // overrides were removed when that landed: PAGE_REGISTRY now dedupes by
  // internal_code (last wins), so an override with `translations: []` would
  // fully SHADOW the core entry and strip the group's menu labels on a fresh
  // install.
  //
  // DMO (BaseApp's demo module) is stripped from this app (CLAUDE.md §16), which
  // leaves NAV.CATALOG — DMO's only occupant — permanently empty. See §16 for why
  // this override (not a DB fix) is what keeps it deactivated.
  {
    internal_code: 'NAV.CATALOG',
    route_path:    '/catalog-admin',
    sort_order:    1150,
    is_active:     'N',
    parent_internal_code: 'NAV.SETTINGS',
    translations: [
      { locale_code: 'EN_US', display_text: 'Product Catalogue (Demo)' },
      { locale_code: 'EN_GB', display_text: 'Product Catalogue (Demo)' },
      { locale_code: 'ES_MX', display_text: 'Catálogo de Productos (Demo)' },
    ],
  },

  // ===== SOFSEK everyday objects — flat at the TOP LEVEL =========================
  // Product-owner directive: the six operational entities everyone uses daily
  // lead the menu, legacy-style, with the owner's exact labels (Users, Roles,
  // Actions, Policies, Groups, Duties). This is a deliberate departure from
  // §17.E's "domain-groups-only at root" convention (menu placement is not a
  // build gate). Underlying pages/tables keep their sfk names; only the visible
  // label and menu position change. Routes are unchanged so existing links hold.
  {
    // RETIRED — users are managed in ONE place, the platform Users screen.
    // A monitored subject is a csm_user_master row (migration 20260812010000);
    // this page was a second screen over the same records, so the top-level
    // "Users" slot now points at CSM.USER.LIST (re-declared below) and SOFSEK's
    // subject-only attributes reach it through the record-extension manifest in
    // src/app/extensions/sfk_subject_profile.ts. Kept as an explicit inactive
    // entry — rather than deleted — so csm_sync_page_master deactivates the
    // already-deployed row instead of leaving it orphaned in the menu.
    internal_code: 'SFK.APP_USER.LIST',
    route_path: '/sfk/app-users',
    sort_order: 10,
    is_active: 'N',
    translations: [
      { locale_code: 'EN_US', display_text: 'Users (retired)' },
      { locale_code: 'EN_GB', display_text: 'Users (retired)' },
      { locale_code: 'ES_MX', display_text: 'Usuarios (retirado)' },
    ],
  },
  {
    // Re-declaration of an INHERITED page to override its placement only
    // (page_registry.ts dedupes by internal_code, last declaration winning).
    // Everything else — route, component, behaviour — stays BaseApp's. Moving a
    // page whose current parent is still alive also needs the one-time migration
    // 20260816000000_sfk_single_user_page.sql (§17.E).
    internal_code: 'CSM.USER.LIST',
    route_path: '/security/users',
    sort_order: 10,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Users' },
      { locale_code: 'EN_GB', display_text: 'Users' },
      { locale_code: 'ES_MX', display_text: 'Usuarios' },
    ],
  },
  {
    // RETIRED — roles are managed in ONE place, the platform Roles screen. An
    // external role is a csm_role_master row (migration 20260901000000); this
    // page was a second screen over the same records, so the top-level "Roles"
    // slot now points at CSM.ROLE.LIST (re-declared below) and SOFSEK's
    // external-role attributes reach it through src/app/extensions/sfk_role.ts.
    // Kept as an explicit inactive entry so csm_sync_page_master deactivates the
    // already-deployed row rather than orphaning it.
    internal_code: 'SFK.ROLE.LIST',
    route_path: '/sfk/roles',
    sort_order: 20,
    is_active: 'N',
    translations: [
      { locale_code: 'EN_US', display_text: 'Roles (retired)' },
      { locale_code: 'EN_GB', display_text: 'Roles (retired)' },
      { locale_code: 'ES_MX', display_text: 'Roles (retirado)' },
    ],
  },
  {
    // Re-declaration of the INHERITED Roles page to override its placement only
    // (page_registry dedupes by internal_code, last declaration winning). Needs
    // the one-time page-sync migration to reparent the already-deployed row.
    internal_code: 'CSM.ROLE.LIST',
    route_path: '/security/roles',
    sort_order: 20,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Roles' },
      { locale_code: 'EN_GB', display_text: 'Roles' },
      { locale_code: 'ES_MX', display_text: 'Roles' },
    ],
  },
  {
    // RETIRED — actions/functions are managed in ONE place, the platform Pages
    // screen. An external function is a csm_page_master row (migration
    // 20260821000000); the top-level "Actions" slot now points at
    // CSM.PAGE.MASTER (re-declared below) and SOFSEK's function attributes reach
    // it through src/app/extensions/sfk_action.ts.
    internal_code: 'SFK.ACTION.LIST',
    route_path: '/sfk/actions',
    sort_order: 30,
    is_active: 'N',
    translations: [
      { locale_code: 'EN_US', display_text: 'Actions (retired)' },
      { locale_code: 'EN_GB', display_text: 'Actions (retired)' },
      { locale_code: 'ES_MX', display_text: 'Acciones (retirado)' },
    ],
  },
  {
    // Re-declaration of the INHERITED Pages page (csm_page_master, already
    // application-aware) to serve as the unified Actions/Pages screen.
    internal_code: 'CSM.PAGE.MASTER',
    route_path: '/security/pages',
    sort_order: 30,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Actions' },
      { locale_code: 'EN_GB', display_text: 'Actions' },
      { locale_code: 'ES_MX', display_text: 'Acciones' },
    ],
  },
  {
    internal_code: 'SFK.CONFLICT_RULE.LIST',
    route_path: '/sfk/evaluation/rules',
    sort_order: 40,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Policies' },
      { locale_code: 'EN_GB', display_text: 'Policies' },
      { locale_code: 'ES_MX', display_text: 'Políticas' },
    ],
  },
  {
    internal_code: 'SFK.APP_USER_GROUP.LIST',
    route_path: '/sfk/evaluation/groups',
    sort_order: 50,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Groups' },
      { locale_code: 'EN_GB', display_text: 'Groups' },
      { locale_code: 'ES_MX', display_text: 'Grupos' },
    ],
  },
  {
    internal_code: 'SFK.BUNDLE.LIST',
    route_path: '/sfk/bundles',
    sort_order: 60,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Duties' },
      { locale_code: 'EN_GB', display_text: 'Duties' },
      { locale_code: 'ES_MX', display_text: 'Funciones' },
    ],
  },

  // ===== SOFSEK business domains (§17.E) — top-level, business-domain-first =====
  {
    internal_code: 'SFK.NAV.ACCESS_MODEL',
    route_path:    '/sfk/access-model',
    sort_order:    200,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Access Model' },
      { locale_code: 'EN_GB', display_text: 'Access Model' },
      { locale_code: 'ES_MX', display_text: 'Modelo de Acceso' },
    ],
  },
  {
    internal_code: 'SFK.APPLICATION.LIST',
    route_path: '/sfk/applications',
    parent_internal_code: 'SFK.NAV.ACCESS_MODEL',
    sort_order: 210,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Application Registry' },
      { locale_code: 'EN_GB', display_text: 'Application Registry' },
      { locale_code: 'ES_MX', display_text: 'Registro de Aplicaciones' },
    ],
  },
  // SFK.APP_USER.LIST (Users) and SFK.BUNDLE.LIST (Duties) were promoted to the
  // top level — see the "everyday objects" section above.
  {
    internal_code: 'SFK.ORG_NODE.LIST',
    route_path: '/sfk/org-nodes',
    parent_internal_code: 'SFK.NAV.ACCESS_MODEL',
    sort_order: 240,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Org Hierarchy Console' },
      { locale_code: 'EN_GB', display_text: 'Org Hierarchy Console' },
      { locale_code: 'ES_MX', display_text: 'Consola de Jerarquía Organizacional' },
    ],
  },
  {
    // Moved here from the retired "SoD Rules & Groups" group (Step 4): it analyses
    // the access model (over-concentrated permissions), so it belongs with it.
    internal_code: 'SFK.SPOF_FINDING.LIST',
    route_path: '/sfk/evaluation/spof',
    parent_internal_code: 'SFK.NAV.ACCESS_MODEL',
    sort_order: 250,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Operational Bottleneck Dashboard' },
      { locale_code: 'EN_GB', display_text: 'Operational Bottleneck Dashboard' },
      { locale_code: 'ES_MX', display_text: 'Panel de Cuellos de Botella Operativos' },
    ],
  },

  // ===== SOFSEK lookup maintenance, under the inherited NAV.BASE_TABLES root (§17.E) =====
  {
    internal_code: 'SFK.NAV.BASE_TABLES.ACCESS_MODEL',
    route_path:    '/sfk/base-tables/access-model',
    parent_internal_code: 'NAV.BASE_TABLES',
    sort_order:    1110,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Access Model' },
      { locale_code: 'EN_GB', display_text: 'Access Model' },
      { locale_code: 'ES_MX', display_text: 'Modelo de Acceso' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.DISTRIBUTION_MODE',
    route_path: '/sfk/base-tables/distribution-mode',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.ACCESS_MODEL',
    sort_order: 1111,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Distribution Modes' },
      { locale_code: 'EN_GB', display_text: 'Distribution Modes' },
      { locale_code: 'ES_MX', display_text: 'Modos de Distribución' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.APP_USER_TYPE',
    route_path: '/sfk/base-tables/app-user-type',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.ACCESS_MODEL',
    sort_order: 1112,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Subject Types' },
      { locale_code: 'EN_GB', display_text: 'Subject Types' },
      { locale_code: 'ES_MX', display_text: 'Tipos de Sujeto' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.CONNECTION_STATUS',
    route_path: '/sfk/base-tables/connection-status',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.ACCESS_MODEL',
    sort_order: 1113,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Connection Statuses' },
      { locale_code: 'EN_GB', display_text: 'Connection Statuses' },
      { locale_code: 'ES_MX', display_text: 'Estados de Conexión' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.ORG_NODE_TYPE',
    route_path: '/sfk/base-tables/org-node-type',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.ACCESS_MODEL',
    sort_order: 1114,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Org Node Types' },
      { locale_code: 'EN_GB', display_text: 'Org Node Types' },
      { locale_code: 'ES_MX', display_text: 'Tipos de Nodo Organizacional' },
    ],
  },

  // ===== SOFSEK Phase 2 — Ingestion & Reconciliation (§17.E business domain) =====
  {
    internal_code: 'SFK.NAV.INGESTION',
    route_path:    '/sfk/ingestion',
    sort_order:    300,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Ingestion & Reconciliation' },
      { locale_code: 'EN_GB', display_text: 'Ingestion & Reconciliation' },
      { locale_code: 'ES_MX', display_text: 'Ingesta y Reconciliación' },
    ],
  },
  {
    internal_code: 'SFK.INGESTION_RUN.LIST',
    route_path: '/sfk/ingestion/runs',
    parent_internal_code: 'SFK.NAV.INGESTION',
    sort_order: 310,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Ingestion Run History' },
      { locale_code: 'EN_GB', display_text: 'Ingestion Run History' },
      { locale_code: 'ES_MX', display_text: 'Historial de Ejecuciones de Ingesta' },
    ],
  },
  {
    internal_code: 'SFK.API_FIELD_MAP.LIST',
    route_path: '/sfk/ingestion/field-map',
    parent_internal_code: 'SFK.NAV.INGESTION',
    sort_order: 320,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Mapping Dictionary Manager' },
      { locale_code: 'EN_GB', display_text: 'Mapping Dictionary Manager' },
      { locale_code: 'ES_MX', display_text: 'Administrador de Diccionario de Mapeo' },
    ],
  },
  {
    internal_code: 'SFK.VARIANCE.LIST',
    route_path: '/sfk/ingestion/variances',
    parent_internal_code: 'SFK.NAV.INGESTION',
    sort_order: 330,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Variance Dashboard' },
      { locale_code: 'EN_GB', display_text: 'Variance Dashboard' },
      { locale_code: 'ES_MX', display_text: 'Panel de Variaciones' },
    ],
  },
  {
    internal_code: 'SFK.INTAKE_REQUEST.LIST',
    route_path: '/sfk/ingestion/intake',
    parent_internal_code: 'SFK.NAV.INGESTION',
    sort_order: 340,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Intake Queue' },
      { locale_code: 'EN_GB', display_text: 'Intake Queue' },
      { locale_code: 'ES_MX', display_text: 'Cola de Ingreso' },
    ],
  },

  // ===== SOFSEK Phase 2 lookup maintenance =====
  {
    internal_code: 'SFK.NAV.BASE_TABLES.INGESTION',
    route_path:    '/sfk/base-tables/ingestion',
    parent_internal_code: 'NAV.BASE_TABLES',
    sort_order:    1120,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Ingestion & Reconciliation' },
      { locale_code: 'EN_GB', display_text: 'Ingestion & Reconciliation' },
      { locale_code: 'ES_MX', display_text: 'Ingesta y Reconciliación' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.RUN_TYPE',
    route_path: '/sfk/base-tables/run-type',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.INGESTION',
    sort_order: 1121,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Ingestion Run Types' },
      { locale_code: 'EN_GB', display_text: 'Ingestion Run Types' },
      { locale_code: 'ES_MX', display_text: 'Tipos de Ejecución de Ingesta' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.TRACKING_TOKEN',
    route_path: '/sfk/base-tables/tracking-token',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.INGESTION',
    sort_order: 1122,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Tracking Tokens' },
      { locale_code: 'EN_GB', display_text: 'Tracking Tokens' },
      { locale_code: 'ES_MX', display_text: 'Tokens de Seguimiento' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.VARIANCE_TYPE',
    route_path: '/sfk/base-tables/variance-type',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.INGESTION',
    sort_order: 1123,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Variance Types' },
      { locale_code: 'EN_GB', display_text: 'Variance Types' },
      { locale_code: 'ES_MX', display_text: 'Tipos de Variación' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.VARIANCE_DISPOSITION',
    route_path: '/sfk/base-tables/variance-disposition',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.INGESTION',
    sort_order: 1124,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Variance Dispositions' },
      { locale_code: 'EN_GB', display_text: 'Variance Dispositions' },
      { locale_code: 'ES_MX', display_text: 'Disposiciones de Variación' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.INTAKE_TYPE',
    route_path: '/sfk/base-tables/intake-type',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.INGESTION',
    sort_order: 1125,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Intake Types' },
      { locale_code: 'EN_GB', display_text: 'Intake Types' },
      { locale_code: 'ES_MX', display_text: 'Tipos de Ingreso' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.INTAKE_STATUS',
    route_path: '/sfk/base-tables/intake-status',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.INGESTION',
    sort_order: 1126,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Intake Statuses' },
      { locale_code: 'EN_GB', display_text: 'Intake Statuses' },
      { locale_code: 'ES_MX', display_text: 'Estados de Ingreso' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.INTAKE_REASON_CODE',
    route_path: '/sfk/base-tables/intake-reason-code',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.INGESTION',
    sort_order: 1127,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Intake Reason Codes' },
      { locale_code: 'EN_GB', display_text: 'Intake Reason Codes' },
      { locale_code: 'ES_MX', display_text: 'Códigos de Motivo de Ingreso' },
    ],
  },

  // ===== SOFSEK — "SoD Rules & Groups" business-domain group RETIRED (Step 4) =====
  // Its pages all moved out: Policies (SFK.CONFLICT_RULE.LIST) and Groups
  // (SFK.APP_USER_GROUP.LIST) went to the top level; Operational Bottleneck
  // Dashboard (SFK.SPOF_FINDING.LIST) moved into Access Model. The now-empty
  // SFK.NAV.EVALUATION group is dropped from the registry; the page sync
  // soft-deactivates it as "missing", and 20260725000000_sfk_menu_reflow.sql
  // repoints SPOF + deactivates the group on already-deployed databases.
  // (The NAV.BASE_TABLES.EVALUATION lookup sub-group below is a separate node
  // under Base Tables and stays — it still backs the conflict-rule lookups.)

  // ===== SOFSEK Phase 3 lookup maintenance =====
  {
    internal_code: 'SFK.NAV.BASE_TABLES.EVALUATION',
    route_path:    '/sfk/base-tables/evaluation',
    parent_internal_code: 'NAV.BASE_TABLES',
    sort_order:    1130,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'SoD Rules & Groups' },
      { locale_code: 'EN_GB', display_text: 'SoD Rules & Groups' },
      { locale_code: 'ES_MX', display_text: 'Reglas SoD y Grupos' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.RULE_SEVERITY',
    route_path: '/sfk/base-tables/rule-severity',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.EVALUATION',
    sort_order: 1131,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Rule Severities' },
      { locale_code: 'EN_GB', display_text: 'Rule Severities' },
      { locale_code: 'ES_MX', display_text: 'Severidades de Reglas' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.RULE_OP_STATE',
    route_path: '/sfk/base-tables/rule-op-state',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.EVALUATION',
    sort_order: 1132,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Rule Operational States' },
      { locale_code: 'EN_GB', display_text: 'Rule Operational States' },
      { locale_code: 'ES_MX', display_text: 'Estados Operativos de Reglas' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.CONTINUITY_STATUS',
    route_path: '/sfk/base-tables/continuity-status',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.EVALUATION',
    sort_order: 1133,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Continuity Statuses' },
      { locale_code: 'EN_GB', display_text: 'Continuity Statuses' },
      { locale_code: 'ES_MX', display_text: 'Estados de Continuidad' },
    ],
  },

  // ===== SOFSEK Phase 4 — Breach Lifecycle (§17.E business domain) =====
  {
    internal_code: 'SFK.NAV.BREACH',
    route_path:    '/sfk/breach',
    sort_order:    500,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Breach Lifecycle' },
      { locale_code: 'EN_GB', display_text: 'Breach Lifecycle' },
      { locale_code: 'ES_MX', display_text: 'Ciclo de Vida de Infracciones' },
    ],
  },
  {
    internal_code: 'SFK.VIOLATION.LIST',
    route_path: '/sfk/breach/violations',
    parent_internal_code: 'SFK.NAV.BREACH',
    sort_order: 510,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Violations Tracker' },
      { locale_code: 'EN_GB', display_text: 'Violations Tracker' },
      { locale_code: 'ES_MX', display_text: 'Rastreador de Infracciones' },
    ],
  },
  {
    internal_code: 'SFK.MITIGATION_TEMPLATE.LIST',
    route_path: '/sfk/breach/templates',
    parent_internal_code: 'SFK.NAV.BREACH',
    sort_order: 520,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Mitigation Control Templates' },
      { locale_code: 'EN_GB', display_text: 'Mitigation Control Templates' },
      { locale_code: 'ES_MX', display_text: 'Plantillas de Controles Mitigantes' },
    ],
  },

  // ===== SOFSEK Phase 4 lookup maintenance =====
  {
    internal_code: 'SFK.NAV.BASE_TABLES.BREACH',
    route_path:    '/sfk/base-tables/breach',
    parent_internal_code: 'NAV.BASE_TABLES',
    sort_order:    1140,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Breach Lifecycle' },
      { locale_code: 'EN_GB', display_text: 'Breach Lifecycle' },
      { locale_code: 'ES_MX', display_text: 'Ciclo de Vida de Infracciones' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.VIOLATION_STATUS',
    route_path: '/sfk/base-tables/violation-status',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.BREACH',
    sort_order: 1141,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Violation Statuses' },
      { locale_code: 'EN_GB', display_text: 'Violation Statuses' },
      { locale_code: 'ES_MX', display_text: 'Estados de Infracción' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.REASON_CODE',
    route_path: '/sfk/base-tables/reason-code',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.BREACH',
    sort_order: 1142,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Exception Reason Codes' },
      { locale_code: 'EN_GB', display_text: 'Exception Reason Codes' },
      { locale_code: 'ES_MX', display_text: 'Códigos de Motivo de Excepción' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.MITIGATING_ACTION_CODE',
    route_path: '/sfk/base-tables/mitigating-action-code',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.BREACH',
    sort_order: 1143,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Mitigating Action Codes' },
      { locale_code: 'EN_GB', display_text: 'Mitigating Action Codes' },
      { locale_code: 'ES_MX', display_text: 'Códigos de Acción Mitigante' },
    ],
  },

  // ===== SOFSEK Phase 5 — Sandbox, Remediation & Delegation (§17.E business domain) =====
  {
    internal_code: 'SFK.NAV.REMEDIATION',
    route_path:    '/sfk/remediation',
    sort_order:    600,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Sandbox & Remediation' },
      { locale_code: 'EN_GB', display_text: 'Sandbox & Remediation' },
      { locale_code: 'ES_MX', display_text: 'Sandbox y Remediación' },
    ],
  },
  {
    internal_code: 'SFK.SANDBOX_SESSION.LIST',
    route_path: '/sfk/remediation/sandbox',
    parent_internal_code: 'SFK.NAV.REMEDIATION',
    sort_order: 610,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Remediation Workspace' },
      { locale_code: 'EN_GB', display_text: 'Remediation Workspace' },
      { locale_code: 'ES_MX', display_text: 'Espacio de Remediación' },
    ],
  },
  {
    internal_code: 'SFK.DELEGATION.LIST',
    route_path: '/sfk/remediation/delegations',
    parent_internal_code: 'SFK.NAV.REMEDIATION',
    sort_order: 620,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Delegation Planner' },
      { locale_code: 'EN_GB', display_text: 'Delegation Planner' },
      { locale_code: 'ES_MX', display_text: 'Planificador de Delegación' },
    ],
  },

  // ===== SOFSEK Phase 5 lookup maintenance =====
  {
    internal_code: 'SFK.NAV.BASE_TABLES.REMEDIATION',
    route_path:    '/sfk/base-tables/remediation',
    parent_internal_code: 'NAV.BASE_TABLES',
    sort_order:    1150,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Sandbox & Remediation' },
      { locale_code: 'EN_GB', display_text: 'Sandbox & Remediation' },
      { locale_code: 'ES_MX', display_text: 'Sandbox y Remediación' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.SANDBOX_STATUS',
    route_path: '/sfk/base-tables/sandbox-status',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.REMEDIATION',
    sort_order: 1151,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Sandbox Statuses' },
      { locale_code: 'EN_GB', display_text: 'Sandbox Statuses' },
      { locale_code: 'ES_MX', display_text: 'Estados de Sandbox' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.SANDBOX_OPERATION',
    route_path: '/sfk/base-tables/sandbox-operation',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.REMEDIATION',
    sort_order: 1152,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Sandbox Operations' },
      { locale_code: 'EN_GB', display_text: 'Sandbox Operations' },
      { locale_code: 'ES_MX', display_text: 'Operaciones de Sandbox' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.SANDBOX_DISPOSITION',
    route_path: '/sfk/base-tables/sandbox-disposition',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.REMEDIATION',
    sort_order: 1153,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Sandbox Dispositions' },
      { locale_code: 'EN_GB', display_text: 'Sandbox Dispositions' },
      { locale_code: 'ES_MX', display_text: 'Disposiciones de Sandbox' },
    ],
  },

  // ===== SOFSEK business domain — Phase 6 (§13/§14/§15/§16, D6/D8/D11) =====
  {
    internal_code: 'SFK.NAV.MONITORING',
    route_path:    '/sfk/monitoring',
    sort_order:    700,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Transaction Monitoring' },
      { locale_code: 'EN_GB', display_text: 'Transaction Monitoring' },
      { locale_code: 'ES_MX', display_text: 'Monitoreo de Transacciones' },
    ],
  },
  {
    internal_code: 'SFK.OPEN_TRANSACTION.LIST',
    route_path: '/sfk/monitoring/open-transactions',
    parent_internal_code: 'SFK.NAV.MONITORING',
    sort_order: 710,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Open Transactions' },
      { locale_code: 'EN_GB', display_text: 'Open Transactions' },
      { locale_code: 'ES_MX', display_text: 'Transacciones Abiertas' },
    ],
  },
  {
    internal_code: 'SFK.ALERT.LIST',
    route_path: '/sfk/monitoring/alerts',
    parent_internal_code: 'SFK.NAV.MONITORING',
    sort_order: 720,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Live Alert Feed' },
      { locale_code: 'EN_GB', display_text: 'Live Alert Feed' },
      { locale_code: 'ES_MX', display_text: 'Feed de Alertas en Vivo' },
    ],
  },

  // ===== SOFSEK Phase 6 lookup maintenance =====
  {
    internal_code: 'SFK.NAV.BASE_TABLES.MONITORING',
    route_path:    '/sfk/base-tables/monitoring',
    parent_internal_code: 'NAV.BASE_TABLES',
    sort_order:    1160,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Transaction Monitoring' },
      { locale_code: 'EN_GB', display_text: 'Transaction Monitoring' },
      { locale_code: 'ES_MX', display_text: 'Monitoreo de Transacciones' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.TRANSACTION_STATUS',
    route_path: '/sfk/base-tables/transaction-status',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.MONITORING',
    sort_order: 1161,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Transaction Statuses' },
      { locale_code: 'EN_GB', display_text: 'Transaction Statuses' },
      { locale_code: 'ES_MX', display_text: 'Estados de Transacción' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.TRANSACTION_CLOSURE_REASON',
    route_path: '/sfk/base-tables/transaction-closure-reason',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.MONITORING',
    sort_order: 1162,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Transaction Closure Reasons' },
      { locale_code: 'EN_GB', display_text: 'Transaction Closure Reasons' },
      { locale_code: 'ES_MX', display_text: 'Razones de Cierre de Transacción' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.ALERT_STATUS',
    route_path: '/sfk/base-tables/alert-status',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.MONITORING',
    sort_order: 1163,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Alert Statuses' },
      { locale_code: 'EN_GB', display_text: 'Alert Statuses' },
      { locale_code: 'ES_MX', display_text: 'Estados de Alerta' },
    ],
  },

  // ===== SOFSEK business domain — Phase 7 (§19/§31/§34/§36/§41, D12) =====
  {
    internal_code: 'SFK.NAV.DISTRIBUTION',
    route_path:    '/sfk/distribution',
    sort_order:    800,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Distribution & Reporting' },
      { locale_code: 'EN_GB', display_text: 'Distribution & Reporting' },
      { locale_code: 'ES_MX', display_text: 'Distribución e Informes' },
    ],
  },
  {
    internal_code: 'SFK.RELEASE.LIST',
    route_path: '/sfk/distribution/releases',
    parent_internal_code: 'SFK.NAV.DISTRIBUTION',
    sort_order: 810,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Release Console' },
      { locale_code: 'EN_GB', display_text: 'Release Console' },
      { locale_code: 'ES_MX', display_text: 'Consola de Liberaciones' },
    ],
  },
  {
    internal_code: 'SFK.GATEKEEPER_LOG.LIST',
    route_path: '/sfk/distribution/gatekeeper-logs',
    parent_internal_code: 'SFK.NAV.DISTRIBUTION',
    sort_order: 820,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Provisioning Gateway Logs' },
      { locale_code: 'EN_GB', display_text: 'Provisioning Gateway Logs' },
      { locale_code: 'ES_MX', display_text: 'Registros de Puerta de Aprovisionamiento' },
    ],
  },
  {
    internal_code: 'SFK.RISK_HEATMAP.LIST',
    route_path: '/sfk/distribution/risk-heatmap',
    parent_internal_code: 'SFK.NAV.DISTRIBUTION',
    sort_order: 830,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Risk Financial Heatmap' },
      { locale_code: 'EN_GB', display_text: 'Risk Financial Heatmap' },
      { locale_code: 'ES_MX', display_text: 'Mapa de Calor de Riesgo Financiero' },
    ],
  },
  {
    internal_code: 'SFK.AUDIT_PACK.LIST',
    route_path: '/sfk/distribution/audit-pack',
    parent_internal_code: 'SFK.NAV.DISTRIBUTION',
    sort_order: 840,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Audit Pack' },
      { locale_code: 'EN_GB', display_text: 'Audit Pack' },
      { locale_code: 'ES_MX', display_text: 'Paquete de Auditoría' },
    ],
  },

  // ===== SOFSEK Phase 7 lookup maintenance =====
  {
    internal_code: 'SFK.NAV.BASE_TABLES.DISTRIBUTION',
    route_path:    '/sfk/base-tables/distribution',
    parent_internal_code: 'NAV.BASE_TABLES',
    sort_order:    1170,
    is_active:     'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Distribution & Reporting' },
      { locale_code: 'EN_GB', display_text: 'Distribution & Reporting' },
      { locale_code: 'ES_MX', display_text: 'Distribución e Informes' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.RELEASE_STATUS',
    route_path: '/sfk/base-tables/release-status',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.DISTRIBUTION',
    sort_order: 1171,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Release Statuses' },
      { locale_code: 'EN_GB', display_text: 'Release Statuses' },
      { locale_code: 'ES_MX', display_text: 'Estados de Liberación' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.RELEASE_ITEM_STATUS',
    route_path: '/sfk/base-tables/release-item-status',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.DISTRIBUTION',
    sort_order: 1172,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Release Item Statuses' },
      { locale_code: 'EN_GB', display_text: 'Release Item Statuses' },
      { locale_code: 'ES_MX', display_text: 'Estados de Elemento de Liberación' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.REGULATORY_FRAMEWORK',
    route_path: '/sfk/base-tables/regulatory-framework',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.DISTRIBUTION',
    sort_order: 1173,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Regulatory Frameworks' },
      { locale_code: 'EN_GB', display_text: 'Regulatory Frameworks' },
      { locale_code: 'ES_MX', display_text: 'Marcos Regulatorios' },
    ],
  },
  {
    internal_code: 'SFK.LOOKUP.GATEKEEPER_DISPOSITION',
    route_path: '/sfk/base-tables/gatekeeper-disposition',
    parent_internal_code: 'SFK.NAV.BASE_TABLES.DISTRIBUTION',
    sort_order: 1174,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Gatekeeper Dispositions' },
      { locale_code: 'EN_GB', display_text: 'Gatekeeper Dispositions' },
      { locale_code: 'ES_MX', display_text: 'Disposiciones de Puerta de Enlace' },
    ],
  },

  // ===== SOFSEK Reports — the legacy westlock "Reports" group, rebuilt =========
  // Seven read-only relational reports over the new access model. Kept at the
  // TOP LEVEL (like the legacy menu), sorted after the lifecycle domains and
  // before Base Tables. To nest them under Distribution & Reporting instead,
  // set each entry's parent_internal_code to 'SFK.NAV.DISTRIBUTION'.
  {
    internal_code: 'SFK.NAV.REPORTS',
    route_path: '/sfk/reports',
    sort_order: 900,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Reports' },
      { locale_code: 'EN_GB', display_text: 'Reports' },
      { locale_code: 'ES_MX', display_text: 'Informes' },
    ],
  },
  {
    internal_code: 'SFK.REPORT.USER_AUTHORIZATION',
    route_path: '/sfk/reports/user-authorization',
    parent_internal_code: 'SFK.NAV.REPORTS',
    sort_order: 910,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'User Authorization' },
      { locale_code: 'EN_GB', display_text: 'User Authorization' },
      { locale_code: 'ES_MX', display_text: 'Autorización de Usuario' },
    ],
  },
  {
    internal_code: 'SFK.REPORT.USER_ROLES',
    route_path: '/sfk/reports/user-roles',
    parent_internal_code: 'SFK.NAV.REPORTS',
    sort_order: 920,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'User Roles' },
      { locale_code: 'EN_GB', display_text: 'User Roles' },
      { locale_code: 'ES_MX', display_text: 'Roles de Usuario' },
    ],
  },
  {
    internal_code: 'SFK.REPORT.ROLE_ACTIONS',
    route_path: '/sfk/reports/role-actions',
    parent_internal_code: 'SFK.NAV.REPORTS',
    sort_order: 930,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Role Actions' },
      { locale_code: 'EN_GB', display_text: 'Role Actions' },
      { locale_code: 'ES_MX', display_text: 'Acciones de Rol' },
    ],
  },
  {
    internal_code: 'SFK.REPORT.USER_ACTIONS',
    route_path: '/sfk/reports/user-actions',
    parent_internal_code: 'SFK.NAV.REPORTS',
    sort_order: 940,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'User Actions' },
      { locale_code: 'EN_GB', display_text: 'User Actions' },
      { locale_code: 'ES_MX', display_text: 'Acciones de Usuario' },
    ],
  },
  {
    internal_code: 'SFK.REPORT.ROLE_USERS',
    route_path: '/sfk/reports/role-users',
    parent_internal_code: 'SFK.NAV.REPORTS',
    sort_order: 950,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Role Users' },
      { locale_code: 'EN_GB', display_text: 'Role Users' },
      { locale_code: 'ES_MX', display_text: 'Usuarios de Rol' },
    ],
  },
  {
    internal_code: 'SFK.REPORT.USER_CONFLICTS',
    route_path: '/sfk/reports/user-conflicts',
    parent_internal_code: 'SFK.NAV.REPORTS',
    sort_order: 960,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'User Conflicts' },
      { locale_code: 'EN_GB', display_text: 'User Conflicts' },
      { locale_code: 'ES_MX', display_text: 'Conflictos de Usuario' },
    ],
  },
  {
    internal_code: 'SFK.REPORT.POLICY_CONFLICTS',
    route_path: '/sfk/reports/policy-conflicts',
    parent_internal_code: 'SFK.NAV.REPORTS',
    sort_order: 970,
    is_active: 'Y',
    translations: [
      { locale_code: 'EN_US', display_text: 'Policy Conflicts' },
      { locale_code: 'EN_GB', display_text: 'Policy Conflicts' },
      { locale_code: 'ES_MX', display_text: 'Conflictos de Política' },
    ],
  },
];
